import {
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "react-toastify";
import {
  CalendarDaysIcon,
  ChevronDownIcon,
  CheckCircleIcon,
  HandRaisedIcon,
  PlusIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import {
  ActivityType,
  ActivityTypeLabels,
  CallFollowUpType,
  CallFollowUpTypeLabels,
  CallOutcome,
  CallOutcomeLabels,
  currencyFormatter,
  currencySymbol,
  ICallFollowUp,
  ICallFollowUpInvoice,
  ICallFollowUps,
  ICallTranscriptSegment,
  InvoiceQueryReason,
  InvoiceQueryReasonLabels,
  IPaymentPlanTerms,
  PaymentPlanFrequency,
} from "shared";

import { Badge } from "../../../../common/Atoms/Badge";
import { Button } from "../../../../common/Atoms/Button";
import { CheckBox } from "../../../../common/Atoms/CheckBox";
import { CurrencyInput } from "../../../../common/Atoms/CurrencyInput";
import { DatePicker } from "../../../../common/Atoms/DatePicker";
import { DateTimeInput, nowDateTimeLocal } from "../../../../common/Atoms/DateTimeInput";
import { InputTextAddon } from "../../../../common/Atoms/InputTextAddon";
import { Modal } from "../../../../common/Atoms/Modal";
import { Select } from "../../../../common/Atoms/Select";
import { TextArea } from "../../../../common/Atoms/TextArea";
import { classNames } from "../../../../common/lib/classNames";
import { getFrequencyTypeLabel } from "../../../../common/Modals/CreatePaymentPlanModal";
import { IHeroIcon } from "../../../types/types";
import { useSettings } from "../../../hooks/useSettings";
import { useCreateContactActivityMutation, useFetchFollowUpInvoicesQuery } from "../../../services/api/contactsApi/contact";
import { useCreateInvoicePromiseMutation, useOpenInvoiceQueryMutation } from "../../../services/api/invoiceApi/invoice";
import { InvoicePaymentPlanModal } from "../../Invoice/InvoiceDocument/InvoicePaymentPlanModal";
import { TranscriptBubbles } from "../../Molecules/CallTranscript";

/** One promise, query or plan the user is recording alongside the call. Only the fields for its type are used */
interface IFollowUpDraft {
  key: number;
  type: CallFollowUpType;
  invoiceId: string | null;
  /** Picked out of the transcript by the AI, shown as such until the user changes anything */
  suggested: boolean;
  promisedDate: string;
  /** Null means the amount due of the picked invoice */
  promisedAmountCents: number | null;
  updateExpectedPaymentDate: boolean;
  queryReason: InvoiceQueryReason | null;
  queryDetail: string;
  nextAction: string;
  dueDate: string;
  planFrequency: PaymentPlanFrequency | null;
  planPayments: number | null;
  /** Written to the API. Retrying a failed save skips these */
  saved: boolean;
  error: string | null;
}

/** A plan the user asked for. The plan builder opens for each once the rest is saved */
interface IPlanToBuild {
  key: number;
  invoiceId: string;
  contactCallId?: string;
  terms: Partial<IPaymentPlanTerms>;
}

const sections: { type: CallFollowUpType; icon: IHeroIcon; description: string }[] = [
  { type: CallFollowUpType.PAYMENT_PROMISE, icon: HandRaisedIcon, description: `The customer committed to pay by a date` },
  { type: CallFollowUpType.INVOICE_QUERY, icon: QuestionMarkCircleIcon, description: `The customer disputed or questioned an invoice` },
  { type: CallFollowUpType.PAYMENT_PLAN, icon: CalendarDaysIcon, description: `The customer wants to pay in instalments` },
];

const reasonOptions = Object.values(InvoiceQueryReason).map(value => ({ label: InvoiceQueryReasonLabels[value], value }));
const frequencyOptions = Object.values(PaymentPlanFrequency).map(value => ({ label: getFrequencyTypeLabel(value), value }));

let nextKey = 1;

function blankDraft(type: CallFollowUpType, invoiceId: string | null, updateExpectedPaymentDate: boolean): IFollowUpDraft {
  return {
    key: nextKey++,
    type,
    invoiceId,
    suggested: false,
    promisedDate: ``,
    promisedAmountCents: null,
    updateExpectedPaymentDate,
    queryReason: null,
    queryDetail: ``,
    nextAction: ``,
    dueDate: ``,
    planFrequency: null,
    planPayments: null,
    saved: false,
    error: null,
  };
}

function suggestedDraft(item: ICallFollowUp, updateExpectedPaymentDate: boolean): IFollowUpDraft {
  return {
    ...blankDraft(item.type, item.invoiceId, updateExpectedPaymentDate),
    suggested: true,
    promisedDate: item.promisedDate || ``,
    promisedAmountCents: item.promisedAmountCents,
    queryReason: item.queryReason,
    queryDetail: item.queryDetail || ``,
    planFrequency: item.planFrequency,
    planPayments: item.planPayments,
  };
}

/** Why the draft cannot be saved yet, or null when it can */
function draftProblem(draft: IFollowUpDraft, invoice: ICallFollowUpInvoice | undefined): string | null {
  if (!invoice) return `Pick an invoice`;

  if (draft.type === CallFollowUpType.PAYMENT_PROMISE) {
    if (!draft.promisedDate) return `Pick the promised date`;

    const amount = draft.promisedAmountCents ?? invoice.amountDueCents;

    if (!Number.isInteger(amount) || amount < 1 || amount > invoice.amountDueCents) return `Amount must be between 0.01 and the amount due`;
  }

  if (draft.type === CallFollowUpType.INVOICE_QUERY) {
    if (!draft.queryReason) return `Pick a reason`;
    if (draft.queryReason === InvoiceQueryReason.OTHER && !draft.queryDetail.trim()) return `Say what the query is about`;
  }

  return null;
}

function apiError(err: unknown, fallback: string) {
  const data = (err as { data?: { message?: string } | string })?.data;

  return (typeof data === `string` ? data : data?.message) || fallback;
}

interface ILogActivityModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  organisationId: string;
  contactId: number;
  /** When set, the activity (and any note) is linked to this invoice */
  invoiceId?: string;
  /** Click to call row this activity is logged from. Everything recorded is linked to it */
  contactCallId?: string;
  /** Type, call outcome and note to start with each time the modal opens */
  initialType?: ActivityType;
  initialCallOutcome?: CallOutcome | null;
  initialNote?: string;
  /** Click to call only: promises, queries and plans the AI heard on the call, offered prefilled */
  followUps?: ICallFollowUps | null;
  /** Click to call only: shown collapsed for reference while filling in the rest */
  transcript?: ICallTranscriptSegment[] | null;
  callerName?: string;
  onSaved?: () => void;
}

/**
 * Logs a call, text or email on the contact. For calls it also records any payment promises, invoice queries and
 * payment plans agreed on it, all from one Save. The AI's picks from a click to call transcript start filled in, the
 * rest is a click away. Promises, queries and the activity are saved in turn, anything that fails can be retried
 * without saving the rest again, and payment plans open the plan builder once everything else is in.
 */
export function LogActivityModal({
  open, setOpen, organisationId, contactId, invoiceId, contactCallId, initialType = ActivityType.PHONE_CALL,
  initialCallOutcome = null, initialNote = ``, followUps, transcript, callerName, onSaved,
}: ILogActivityModalProps) {
  const { data: settings } = useSettings();
  const [type, setType] = useState<ActivityType>(initialType);
  const [callOutcome, setCallOutcome] = useState<CallOutcome | null>(initialCallOutcome);
  const [note, setNote] = useState(initialNote);
  const [activityTime, setActivityTime] = useState(nowDateTimeLocal());
  const [drafts, setDrafts] = useState<IFollowUpDraft[]>([]);
  const [activitySaved, setActivitySaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const [plansToBuild, setPlansToBuild] = useState<IPlanToBuild[]>([]);
  const [createActivity] = useCreateContactActivityMutation();
  const [createPromise] = useCreateInvoicePromiseMutation();
  const [openQuery] = useOpenInvoiceQueryMutation();

  const isCall = type === ActivityType.PHONE_CALL;
  const expectedDateDefault = !!settings?.promiseUpdatesExpectedPaymentDate;

  // Fresh list, so an invoice paid since the call summary cannot be picked. The summary's snapshot covers the wait
  const { data: fetchedInvoices, isLoading: invoicesLoading } = useFetchFollowUpInvoicesQuery(
    { organisationId, contactId },
    { skip: !open || !isCall, refetchOnMountOrArgChange: true },
  );
  const invoices = fetchedInvoices || followUps?.openInvoices || [];
  const invoiceById = useMemo(() => new Map(invoices.map(invoice => [invoice.id, invoice])), [invoices]);
  const invoiceOptions = useMemo(() => invoices.map(invoice => ({
    label: `${invoice.number} · ${currencyFormatter(invoice.currency)(invoice.amountDueCents / 100)} due`,
    value: invoice.id,
  })), [invoices]);

  useEffect(() => {
    if (open) {
      setType(initialType);
      setCallOutcome(initialCallOutcome);
      setNote(initialNote);
      setActivityTime(nowDateTimeLocal());
      setDrafts((followUps?.suggestions || []).map(item => suggestedDraft(item, expectedDateDefault)));
      setActivitySaved(false);
      setShowTranscript(false);
    }
  }, [open]);

  const pending = drafts.filter(draft => !draft.saved);
  const problems = new Map(pending.map(draft => [draft.key, draftProblem(draft, invoiceById.get(draft.invoiceId || ``))]));
  const canSave = !saving && (!isCall || [...problems.values()].every(problem => !problem));
  const failed = drafts.some(draft => draft.error);

  function updateDraft(key: number, patch: Partial<IFollowUpDraft>) {
    setDrafts(current => current.map(draft => (draft.key === key ? { ...draft, ...patch, suggested: false, error: null } : draft)));
  }

  function addDraft(sectionType: CallFollowUpType) {
    setDrafts(current => [...current, blankDraft(sectionType, invoices.length === 1 ? invoices[0].id : null, expectedDateDefault)]);
  }

  function removeDraft(key: number) {
    setDrafts(current => current.filter(draft => draft.key !== key));
  }

  async function saveDraft(draft: IFollowUpDraft) {
    const invoice = invoiceById.get(draft.invoiceId || ``);

    if (!invoice) return;

    if (draft.type === CallFollowUpType.PAYMENT_PROMISE) {
      await createPromise({
        organisationId,
        invoiceId: invoice.id,
        promisedDate: draft.promisedDate,
        promisedAmountCents: draft.promisedAmountCents ?? invoice.amountDueCents,
        updateExpectedPaymentDate: draft.updateExpectedPaymentDate,
        contactCallId,
      }).unwrap();
    }
    else if (draft.type === CallFollowUpType.INVOICE_QUERY && draft.queryReason) {
      await openQuery({
        organisationId,
        invoiceId: invoice.id,
        reason: draft.queryReason,
        detail: draft.queryDetail.trim() || null,
        nextAction: draft.nextAction.trim() || null,
        dueDate: draft.dueDate || null,
        contactCallId,
      }).unwrap();
    }
  }

  async function onSave() {
    setSaving(true);

    let allSaved = true;

    // The call itself first, so it is logged even if a follow up fails and the modal is closed
    if (!activitySaved) {
      try {
        await createActivity({
          organisationId,
          contactId,
          invoiceId,
          contactCallId,
          type,
          callOutcome: isCall ? callOutcome || undefined : undefined,
          note: note.trim() || undefined,
          activityTime: activityTime ? new Date(activityTime).toISOString() : undefined,
        }).unwrap();

        setActivitySaved(true);
      }
      catch (err) {
        toast.error(apiError(err, `Could not record the activity`));
        setSaving(false);

        return;
      }
    }

    // Plans are built after everything else is in, so a failure here never leaves the builder half way
    const toSave = isCall ? pending.filter(draft => draft.type !== CallFollowUpType.PAYMENT_PLAN) : [];

    for (const draft of toSave) {
      try {
        await saveDraft(draft);
        setDrafts(current => current.map(item => (item.key === draft.key ? { ...item, saved: true, error: null } : item)));
      }
      catch (err) {
        allSaved = false;
        setDrafts(current => current.map(item => (item.key === draft.key ? { ...item, error: apiError(err, `Could not save`) } : item)));
      }
    }

    setSaving(false);

    if (!allSaved) return;

    const plans: IPlanToBuild[] = isCall ? pending
      .filter(draft => draft.type === CallFollowUpType.PAYMENT_PLAN && draft.invoiceId)
      .map(draft => ({
        key: draft.key,
        invoiceId: draft.invoiceId as string,
        contactCallId,
        terms: {
          ...(draft.planFrequency ? { frequencyType: draft.planFrequency } : {}),
          ...(draft.planPayments ? { numberOfPayments: draft.planPayments } : {}),
        },
      })) : [];
    const recorded = toSave.length + drafts.filter(draft => draft.saved).length;

    toast.success(recorded ? `Activity and ${recorded} follow up${recorded === 1 ? `` : `s`} recorded` : `Activity recorded`);
    setOpen(false);
    onSaved?.();

    if (plans.length) {
      setPlansToBuild(plans);
    }
  }

  function renderDraft(draft: IFollowUpDraft) {
    const invoice = invoiceById.get(draft.invoiceId || ``);
    const problem = problems.get(draft.key) || null;
    const disabled = draft.saved || saving;
    const amountPlaceholder = invoice ? invoice.amountDueCents : 0;

    let fields: ReactNode = null;

    if (draft.type === CallFollowUpType.PAYMENT_PROMISE) {
      fields = (
        <>
          <DatePicker
            label={ `Promised date` }
            name={ `promisedDate${draft.key}` }
            value={ draft.promisedDate || null }
            onChange={ ({ value }) => updateDraft(draft.key, { promisedDate: value || `` }) }
            disabled={ disabled }
            disableClear
          />
          <CurrencyInput
            label={ `Amount` }
            name={ `promisedAmount${draft.key}` }
            prefix={ currencySymbol(invoice?.currency || ``) }
            value={ draft.promisedAmountCents ?? amountPlaceholder }
            onChange={ ({ value }) => updateDraft(draft.key, { promisedAmountCents: Number(value) }) }
            disabled={ disabled }
          />
          <div className={ `sm:col-span-3` }>
            <CheckBox
              label={ `Also set the expected payment date` }
              name={ `expected${draft.key}` }
              checked={ draft.updateExpectedPaymentDate }
              onChange={ ({ value }) => updateDraft(draft.key, { updateExpectedPaymentDate: value }) }
              disabled={ disabled }
              description={ `Syncs the promised date to your accounting system` }
            />
          </div>
        </>
      );
    }
    else if (draft.type === CallFollowUpType.INVOICE_QUERY) {
      fields = (
        <>
          <Select
            label={ `Reason` }
            name={ `reason${draft.key}` }
            options={ reasonOptions }
            selected={ draft.queryReason }
            emptyText={ `Pick a reason` }
            onChange={ ({ value }) => updateDraft(draft.key, { queryReason: value as InvoiceQueryReason }) }
            disabled={ disabled }
          />
          <DatePicker
            label={ `Due date` }
            name={ `dueDate${draft.key}` }
            value={ draft.dueDate || null }
            onChange={ ({ value }) => updateDraft(draft.key, { dueDate: value || `` }) }
            disabled={ disabled }
          />
          <div className={ `sm:col-span-3` }>
            <TextArea
              label={ draft.queryReason === InvoiceQueryReason.OTHER ? `Details (required)` : `Details` }
              name={ `detail${draft.key}` }
              value={ draft.queryDetail }
              rows={ 2 }
              onChange={ ({ value }) => updateDraft(draft.key, { queryDetail: String(value) }) }
              disabled={ disabled }
            />
          </div>
          <div className={ `sm:col-span-3` }>
            <InputTextAddon
              label={ `Next action` }
              name={ `nextAction${draft.key}` }
              value={ draft.nextAction }
              placeholder={ `e.g. Check the delivery note with the customer` }
              onChange={ ({ value }) => updateDraft(draft.key, { nextAction: String(value) }) }
              disabled={ disabled }
            />
          </div>
        </>
      );
    }
    else {
      fields = (
        <>
          <Select
            label={ `Frequency` }
            name={ `frequency${draft.key}` }
            options={ frequencyOptions }
            selected={ draft.planFrequency }
            emptyText={ `Decide in the builder` }
            onChange={ ({ value }) => updateDraft(draft.key, { planFrequency: value as PaymentPlanFrequency }) }
            disabled={ disabled }
            nullable
          />
          <InputTextAddon
            label={ `Payments` }
            name={ `payments${draft.key}` }
            dataType={ `number` }
            value={ draft.planPayments ?? `` }
            placeholder={ `Decide in the builder` }
            onChange={ ({ value }) => updateDraft(draft.key, { planPayments: Number(value) >= 2 ? Math.round(Number(value)) : null }) }
            disabled={ disabled }
          />
          <p className={ `self-end pb-2 text-xs text-gray-500 sm:col-span-3` }>
            { `The plan builder opens with these terms after you save, to check the instalments and surcharge.` }
          </p>
        </>
      );
    }

    return (
      <div
        key={ draft.key }
        className={ classNames(
          `rounded-lg border p-3`,
          draft.saved ? `border-green-300 bg-green-50/40` : draft.error ? `border-red-300 bg-red-50/40` : draft.suggested ? `border-paidniceGreen-300 bg-paidniceGreen-50/40` : `border-gray-200 bg-white`,
        ) }
      >
        <div className={ `mb-2 flex items-center justify-between gap-2` }>
          <div className={ `flex items-center gap-2 text-xs` }>
            { draft.saved ? (
              <Badge color={ `green` }>
                <CheckCircleIcon className={ `mr-1 h-3.5 w-3.5` } />
                { `Saved` }
              </Badge>
            ) : draft.suggested ? (
              <Badge color={ `default` }>
                <SparklesIcon className={ `mr-1 h-3.5 w-3.5` } />
                { `Heard on the call` }
              </Badge>
            ) : null }
            { draft.error ? <span className={ `text-red-600` }>{ draft.error }</span> : null }
          </div>
          { draft.saved ? null : (
            <button
              type={ `button` }
              onClick={ () => removeDraft(draft.key) }
              disabled={ saving }
              aria-label={ `Remove` }
              className={ `cursor-pointer rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed` }
            >
              <XMarkIcon className={ `h-4 w-4` } />
            </button>
          ) }
        </div>

        <div className={ `grid grid-cols-1 gap-3 sm:grid-cols-3` }>
          <Select
            label={ `Invoice` }
            name={ `invoice${draft.key}` }
            options={ invoiceOptions }
            selected={ draft.invoiceId }
            emptyText={ `Pick an invoice` }
            onChange={ ({ value }) => updateDraft(draft.key, { invoiceId: String(value), promisedAmountCents: null }) }
            disabled={ disabled }
          />
          { fields }
        </div>

        { problem && !draft.saved ? <p className={ `mt-2 text-xs text-gray-500` }>{ problem }</p> : null }
      </div>
    );
  }

  return (
    <>
      <Modal
        title={ isCall ? `Log call` : `Record Activity` }
        open={ open }
        setOpen={ setOpen }
        width={ isCall ? `lg` : `md` }
      >
        <div className={ `space-y-5` }>
          <div className={ `grid grid-cols-1 gap-4 sm:grid-cols-2` }>
            { contactCallId ? null : (
              <Select
                label={ `Activity Type` }
                name={ `activityType` }
                options={ Object.values(ActivityType).map(value => ({ label: ActivityTypeLabels[value], value })) }
                selected={ type }
                onChange={ ({ value }) => setType(value as ActivityType) }
                disabled={ saving || activitySaved }
              />
            ) }

            { isCall ? (
              <Select
                label={ `Call Outcome` }
                name={ `callOutcome` }
                emptyText={ `Optionally, select a call outcome...` }
                options={ Object.values(CallOutcome).map(value => ({ label: CallOutcomeLabels[value], value })) }
                selected={ callOutcome }
                onChange={ ({ value }) => setCallOutcome((value as CallOutcome) || null) }
                disabled={ saving || activitySaved }
                nullable
              />
            ) : null }

            <DateTimeInput
              label={ `When` }
              name={ `activityTime` }
              value={ activityTime }
              onChange={ ({ value }) => setActivityTime(value) }
              disabled={ saving || activitySaved }
            />
          </div>

          <TextArea
            label={ `Note` }
            name={ `activityNote` }
            value={ note }
            rows={ contactCallId ? 5 : 3 }
            onChange={ ({ value }) => setNote(String(value)) }
            disabled={ saving || activitySaved }
            placeholder={ `Optional note about this activity` }
            description={ activitySaved ? `Recorded. Fix the follow ups below and save again.` : `When provided, a note will also be added to this contact.` }
          />

          { transcript?.length ? (
            <div>
              <button
                type={ `button` }
                onClick={ () => setShowTranscript(!showTranscript) }
                aria-expanded={ showTranscript }
                className={ `flex cursor-pointer items-center gap-1 text-xs font-medium text-paidniceGreen-700 hover:text-paidniceGreen-900` }
              >
                { showTranscript ? `Hide transcript` : `Show transcript` }
                <ChevronDownIcon className={ classNames(`h-3.5 w-3.5 transition-transform`, showTranscript && `rotate-180`) } />
              </button>
              { showTranscript ? (
                <div className={ `mt-2 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-3` }>
                  <TranscriptBubbles
                    segments={ transcript }
                    userLabel={ callerName || `You` }
                    contactLabel={ `Customer` }
                  />
                </div>
              ) : null }
            </div>
          ) : null }

          { isCall ? (
            <div className={ `space-y-3 border-t border-gray-100 pt-4` }>
              <div>
                <p className={ `text-sm font-medium text-gray-900` }>{ `Agreed on the call` }</p>
                <p className={ `text-xs text-gray-500` }>
                  { followUps?.suggestions.length
                    ? `Picked out of the transcript. Check them, change anything, and add what was missed. Saved together with the call.`
                    : `Record anything the customer agreed to. Saved together with the call.` }
                </p>
              </div>

              { sections.map(section => {
                const sectionDrafts = drafts.filter(draft => draft.type === section.type);

                return (
                  <div
                    key={ section.type }
                    className={ `rounded-xl border border-gray-200 bg-gray-50/60 p-3` }
                  >
                    <div className={ `flex items-center justify-between gap-3` }>
                      <div className={ `flex min-w-0 items-center gap-2.5` }>
                        <span className={ `flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white ring-1 ring-gray-200` }>
                          <section.icon className={ `h-4 w-4 text-paidniceGreen-700` } />
                        </span>
                        <div className={ `min-w-0` }>
                          <p className={ `text-sm font-medium text-gray-900` }>{ CallFollowUpTypeLabels[section.type] }</p>
                          <p className={ `truncate text-xs text-gray-500` }>{ section.description }</p>
                        </div>
                      </div>
                      <Button
                        size={ `sm` }
                        color={ `white` }
                        onClick={ () => addDraft(section.type) }
                        disabled={ saving || invoicesLoading || !invoices.length }
                      >
                        <PlusIcon className={ `mr-1 h-3.5 w-3.5` } />
                        { sectionDrafts.length ? `Another invoice` : `Add` }
                      </Button>
                    </div>

                    { sectionDrafts.length ? (
                      <div className={ `mt-3 space-y-2` }>
                        { sectionDrafts.map(renderDraft) }
                      </div>
                    ) : null }
                  </div>
                );
              }) }

              { !invoicesLoading && !invoices.length ? (
                <p className={ `text-xs text-gray-500` }>{ `This contact has no open invoices to record against.` }</p>
              ) : null }
            </div>
          ) : null }

          <div className={ `flex items-center justify-end gap-2` }>
            { failed ? <span className={ `text-xs text-red-600` }>{ `Some follow ups did not save. Fix them and save again.` }</span> : null }
            <Button
              color={ `paidniceGreenOutline` }
              onClick={ onSave }
              loading={ saving }
              disabled={ !canSave }
            >
              { failed ? `Save again` : isCall && pending.length ? `Save call and follow ups` : isCall ? `Save call` : `Record Activity` }
            </Button>
          </div>
        </div>
      </Modal>

      { plansToBuild[0] ? (
        <InvoicePaymentPlanModal
          key={ plansToBuild[0].key }
          open
          organisationId={ organisationId }
          invoiceId={ plansToBuild[0].invoiceId }
          contactCallId={ plansToBuild[0].contactCallId }
          initialTerms={ plansToBuild[0].terms }
          onClose={ () => setPlansToBuild(current => current.slice(1)) }
          onCreated={ () => toast.success(`Payment plan created`) }
        />
      ) : null }
    </>
  );
}
